import React from 'react'
import { COLORS } from '@btime/constants'
import { capitalizeIfLower, formatDate } from '@btime/helpers'
import { useTranslation } from 'react-i18next'

import { RefetchingIndicator } from '../../../../components/helpers/StatePresenter'
import { fromNow } from '../../../../utils/helpers/date'
import { getSemaphoreConfigColor } from '../../../../utils/helpers/semaphore'
import { useConfig } from '../../../../containers/configs/_load/ConfigsLoader'
import { useRowActionsColumn } from '../../../../components/tables/Table'
import { useServiceOrders } from '../_load/ServiceOrdersLoader'
import { useServiceOrdersActionsList } from '../../../../utils/actions/serviceOrders/list'
import Badge from '../../../../packages/kiti/components/presentation/Badge'
import DataTable from '../../../../packages/kiti/components/table/DataTable'
import EditableServiceOrdersUser from '../../../../containers/serviceOrders/EditableServiceOrdersUser'
import Link from '../../../../components/actions/Link'
import ListCounter from '../ListCounter'
import PublicHistoryRowActions from '../_components/PublicHistoryRowActions'
import StatusTag from '../../../../containers/serviceOrders/StatusTag'
import Text, { SmallText } from '../../../../packages/kiti/components/presentation/Text'
import Modal from '../../../../components/structure/Modal'
import ShowServiceOrderHistoryDetailsModal from '../_components/details/ShowServiceOrderHistoryDetailsModal'
import moment from 'moment'

const STATUS_OPEN_AT = ['open', 'received', 'execution', 'pause', 'pending']

const ServiceOrdersTable = ({ customData, ...props }) => {
  const { t } = useTranslation()
  const semaphoreConfig = useConfig('service_order_started_since_colors')
  const [isShowing, setShowing] = React.useState(false)
  const [details, seeDetails] = React.useState({})

  let data = useServiceOrders()
  data = customData || data

  const handleDetails = record => () => {
    seeDetails(record)
    return setShowing(true)
  }

  const columns = [
    {
      label: `Código`,
      dataIndex: 'yearlyId',
      sortIndex: 'YEARLY_ID',
      flex: 4,
      headerSideContent: (
        <>
          <ListCounter />
          <RefetchingIndicator />
        </>
      ),
      render: (value, record) => {
        const checklistName = capitalizeIfLower(record.checklist?.name)
        const name = record.name

        return (
          <Link onClick={handleDetails(record)} block>
            <SmallText size="xsmall" color={COLORS.dark3}>
              {record.yearlyId}
            </SmallText>
            <Text size="h6" bold color={COLORS.dark2} breakWord>
              {name || checklistName}
            </Text>

            {!!name && (
              <SmallText size="xxsmall" color={COLORS.dark3} breakWord>
                {checklistName}
              </SmallText>
            )}
          </Link>
        )
      }
    },

    {
      label: 'Responsável',
      dataIndex: 'user',
      sortIndex: 'USER_NAME',
      render: (_, record) => <EditableServiceOrdersUser serviceOrder={record} />,
      flex: 3
    },

    {
      label: 'Agendamento',
      dataIndex: 'scheduling',
      sortIndex: 'SCHEDULING',
      flex: 2,
      size: 'xsmall',
      format: formatDate('DD/MM/YY HH:mm')
    },

    {
      label: 'Conclusão',
      dataIndex: 'endDate',
      sortIndex: 'END_DATE',
      flex: 2,
      size: 'xsmall',
      format: formatDate('DD/MM/YY HH:mm')
    },

    {
      label: 'Status',
      dataIndex: 'status',
      sortIndex: 'STATUS_ID',
      flex: 1.5,
      render: (value, record) => <StatusTag item={value} />
    },

    {
      label: 'Aberto em',
      dataIndex: 'insertedAt',
      sortIndex: false,
      flex: 1.5,
      size: 'xsmall',
      format: (value, record) => {
        if (STATUS_OPEN_AT.includes(record.status?.name)) {
          return fromNow(value, { breakpoints: { hour: 48 }, hidePrefix: true })
        }

        return moment(value).format('L LT')
      },
      render: (value, record) => {
        const color = getSemaphoreConfigColor(record.insertedAt, semaphoreConfig?.value)
        return (
          <Badge color={value && color}>
            <Text>{value}</Text>
          </Badge>
        )
      }
    },

    {
      label: 'Ações',
      dataIndex: 'id',
      sortIndex: false,
      flex: 1.5,
      render: (value, record) => <PublicHistoryRowActions serviceOrder={record} />
    }
  ]

  return (
    <>
      <ShowServiceOrderHistoryDetailsModal open={isShowing} onClose={() => setShowing(false)} serviceOrder={details} />
      <DataTable rowKey="id" columns={columns} size="medium" data={data} inPanel {...props} />
    </>
  )
}

export default ServiceOrdersTable
